/**
 * Detecta series Scout/Localizer para etiquetar isScout y excluirlas de MPR/3D.
 */

const SCOUT_DESC_RE =
  /scout|localizer|locator|surview|topogram|scanogram|pilot|localiser|scoutview/i

const VOLUME_MODALITIES = new Set(['CT', 'MR', 'PT', 'NM'])

/**
 * @param {string|null|undefined} raw ImageType (0008,0008)
 * @returns {boolean}
 */
export function imageTypeIsLocalizer(raw) {
  if (!raw || typeof raw !== 'string') return false
  const values = raw.split('\\').map((v) => String(v).trim().toUpperCase())
  return values.includes('LOCALIZER')
}

/**
 * @param {string|null|undefined} description SeriesDescription (0008,103E)
 * @returns {boolean}
 */
export function seriesDescriptionLooksLikeScout(description) {
  if (!description || typeof description !== 'string') return false
  return SCOUT_DESC_RE.test(description)
}

/**
 * @param {object} meta
 * @param {string} [meta.imageType]
 * @param {string} [meta.modality]
 * @param {string} [meta.seriesDescription]
 * @param {string} [meta.description]
 * @param {number} [meta.instanceCount]
 * @param {number[]} [meta.imageOrientationPatient]
 * @returns {boolean}
 */
export function isScoutSeries(meta = {}) {
  if (!meta || typeof meta !== 'object') return false

  if (imageTypeIsLocalizer(meta.imageType)) return true

  const modality = String(meta.modality || '').trim().toUpperCase()
  const desc = meta.seriesDescription || meta.description || ''
  if (VOLUME_MODALITIES.has(modality) && seriesDescriptionLooksLikeScout(desc)) {
    return true
  }

  // Heurística: pocos cortes + descripción vacía no basta; requiere modality CT/MR
  // y conteo tipicamente bajo (< 4) solo si ya hay indicios geométricos opcionales.
  const count = meta.instanceCount
  if (
    VOLUME_MODALITIES.has(modality) &&
    Number.isFinite(count) &&
    count > 0 &&
    count < 4 &&
    seriesDescriptionLooksLikeScout(desc)
  ) {
    return true
  }

  return false
}

/**
 * Lee ImageType desde un dataset Cornerstone / dicom-parser.
 * @param {object|null} image
 * @returns {string|null}
 */
export function readImageType(image) {
  if (!image || !image.data || typeof image.data.string !== 'function') return null
  try {
    const v = image.data.string('x00080008')
    return v != null && String(v).trim() ? String(v).trim() : null
  } catch (e) {
    return null
  }
}

/**
 * Evalúa un item de archivo del visor (con image y/o series).
 * @param {object|null} fileItem
 * @returns {boolean}
 */
export function isScoutFileItem(fileItem) {
  if (!fileItem) return false
  if (fileItem.series && fileItem.series.isScout === true) return true

  const series = fileItem.series || {}
  const image = fileItem.image
  const imageType =
    series.imageType ||
    readImageType(image) ||
    (fileItem.imageType != null ? String(fileItem.imageType) : null)

  let modality = series.modality || ''
  if (!modality && image && image.data && typeof image.data.string === 'function') {
    try {
      modality = image.data.string('x00080060') || ''
    } catch (e) {
      /* ignore */
    }
  }

  const seriesDescription =
    series.seriesDescription || series.description || fileItem.seriesDescription || ''

  const instanceCount =
    series.totalImages != null
      ? series.totalImages
      : series.instanceCount != null
        ? series.instanceCount
        : undefined

  return isScoutSeries({
    imageType,
    modality,
    seriesDescription,
    instanceCount,
  })
}

/**
 * Marca series.isScout in-place en un item de archivo.
 * @param {object} fileItem
 * @returns {object} fileItem
 */
export function stampIsScoutOnFileItem(fileItem) {
  if (!fileItem) return fileItem
  if (!fileItem.series) fileItem.series = {}
  const flagged = isScoutFileItem(fileItem)
  fileItem.series.isScout = flagged
  if (flagged && !fileItem.series.imageType) {
    const t = readImageType(fileItem.image)
    if (t) fileItem.series.imageType = t
  }
  return fileItem
}

/**
 * Marca isScout en todos los items de un array.
 * @param {object[]} items
 * @returns {object[]}
 */
export function stampIsScoutOnItems(items) {
  if (!Array.isArray(items)) return items
  items.forEach(stampIsScoutOnFileItem)
  return items
}

/**
 * Busca la primera serie scout en el store de series del Explorer.
 * @param {{ seriesList?: Map, seriesKeys?: any[] }|null} seriesStore
 * @returns {{ key: *, files: object[] }|null}
 */
export function findScoutSeriesInStore(seriesStore) {
  if (!seriesStore || !seriesStore.seriesList) return null
  const keys =
    Array.isArray(seriesStore.seriesKeys) && seriesStore.seriesKeys.length
      ? seriesStore.seriesKeys
      : [...seriesStore.seriesList.keys()]

  for (const key of keys) {
    const files = seriesStore.seriesList.get(key)
    if (!Array.isArray(files) || files.length === 0) continue
    const sample = files.find((f) => f && f.series) || files[0]
    if (isScoutFileItem(sample)) {
      return { key, files }
    }
  }
  return null
}
